/**
 * SuperAdminUsage — launch-readiness item: visual product-analytics dashboard
 * (Jaime 2026-09-18: "make it visual, easier to understand where users fall
 * off, what's used most, what's not used").
 *
 * Super-admin only. Reads:
 *   GET /analytics/trends  — daily totals, per-event daily series, distinct
 *                            active users/day, page_view counts per module.
 *   GET /analytics/summary — totals by event (7/30d).
 * Charts reuse the existing recharts stack (same library as ChartWidgets).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Line, Legend,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  Activity, AlertCircle, ArrowLeft, BarChart3, CalendarRange, Crown,
  Eye, Loader2, Users,
} from 'lucide-react';
import api from '../services/api';

interface DailyPoint { date: string; total: number }
interface EventSeries { event: string; count: number; daily: Record<string, number> }
interface ActiveUserPoint { date: string; users: number }
interface ModuleViews { module: string; views: number }
interface RoleCount { role: string; count: number }
interface DeviceCount { device: string; count: number }

interface TrendsData {
  days: number;
  daily: DailyPoint[];
  byEvent: EventSeries[];
  activeUsers: ActiveUserPoint[];
  pageViewsByModule: ModuleViews[];
  byRole?: RoleCount[];
  byDevice?: DeviceCount[];
  total: number;
}

const asNum = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : Number(v) || 0);
const asStr = (v: unknown): string => (v == null ? '' : String(v));

const MODULE_LABELS: Record<string, string> = {
  access: 'Accesos', amenities: 'Amenidades', announcements: 'Anuncios', complaints: 'Quejas y sugerencias',
  payments: 'Pagos', reservations: 'Reservas', visitors: 'Visitas', tasks: 'Tareas', wall: 'Muro',
  voting: 'Votaciones', expenses: 'Gastos', marketplace: 'Marketplace', documents: 'Documentos',
  page_view: 'Navegación (vistas)',
};
const moduleLabel = (m: string): string => MODULE_LABELS[m] ?? m.replace(/_/g, ' ');

const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super admin', admin: 'Admin', sub_admin: 'Sub admin', treasurer: 'Tesorero',
  directive: 'Directiva', security: 'Seguridad', resident: 'Residente', employee: 'Empleado',
  anonimo: 'Anónimo',
};
const roleLabel = (r: string): string => ROLE_LABELS[r] ?? r;

const DEVICE_LABELS: Record<string, string> = {
  mobile: 'Móvil', desktop: 'Escritorio', tablet: 'Tablet', other: 'Otro', desconocido: 'Desconocido',
};
const deviceLabel = (d: string): string => DEVICE_LABELS[d] ?? d;

const toTrends = (value: unknown): TrendsData | null => {
  const root = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  const data = (root.data ?? root) as Record<string, unknown>;
  if (!data || typeof data !== 'object') return null;
  const daily = Array.isArray(data.daily) ? (data.daily as Record<string, unknown>[]) : [];
  const byEvent = Array.isArray(data.byEvent) ? (data.byEvent as Record<string, unknown>[]) : [];
  const activeUsers = Array.isArray(data.activeUsers) ? (data.activeUsers as Record<string, unknown>[]) : [];
  const pv = Array.isArray(data.pageViewsByModule) ? (data.pageViewsByModule as Record<string, unknown>[]) : [];
  const byRole = Array.isArray(data.byRole) ? (data.byRole as Record<string, unknown>[]) : [];
  const byDevice = Array.isArray(data.byDevice) ? (data.byDevice as Record<string, unknown>[]) : [];
  return {
    byRole: byRole.map((r) => ({ role: asStr(r.role), count: asNum(r.count) })),
    byDevice: byDevice.map((d) => ({ device: asStr(d.device), count: asNum(d.count) })),
    days: asNum(data.days) || 7,
    daily: daily.map((d) => ({ date: asStr(d.date), total: asNum(d.total) })),
    byEvent: byEvent.map((e) => ({ event: asStr(e.event), count: asNum(e.count), daily: (e.daily && typeof e.daily === 'object') ? e.daily as Record<string, number> : {} })),
    activeUsers: activeUsers.map((a) => ({ date: asStr(a.date), users: asNum(a.users) })),
    pageViewsByModule: pv.map((p) => ({ module: asStr(p.module), views: asNum(p.views) })),
    total: asNum(data.total),
  };
};

/** Last `days` UTC dates, ascending — keeps gaps as zero so charts are continuous. */
function dayAxis(days: number): string[] {
  const out: string[] = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - i));
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
}
const shortDate = (iso: string): string => iso.slice(5); // MM-DD

/** Module of a feature event: first snake token, e.g. complaints_complaint_created -> complaints. */
const eventModule = (event: string): string => event.split('_')[0] || 'otros';

const SuperAdminUsage = () => {
  const navigate = useNavigate();
  const [days, setDays] = useState<7 | 30>(30);
  const [trends, setTrends] = useState<TrendsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async (range: 7 | 30) => {
    setLoading(true); setError(null);
    try {
      const res = await api.get(`/analytics/trends?days=${range}`);
      setTrends(toTrends(res.data));
    } catch (caught: unknown) {
      setError(caught instanceof Error ? caught.message : 'Error al cargar analíticas de uso');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { void fetchAll(days); }, [fetchAll, days]);

  const axis = useMemo(() => dayAxis(days), [days]);

  const dailySeries = useMemo(() => {
    if (!trends) return [];
    const totalByDate = new Map(trends.daily.map((d) => [d.date, d.total]));
    const usersByDate = new Map(trends.activeUsers.map((a) => [a.date, a.users]));
    return axis.map((date) => ({ date: shortDate(date), eventos: totalByDate.get(date) ?? 0, usuarios: usersByDate.get(date) ?? 0 }));
  }, [trends, axis]);

  /** Top feature modules by creation/action events (page_view excluded). */
  const moduleUsage = useMemo(() => {
    if (!trends) return [];
    const byModule = new Map<string, { eventos: number; vistos: number }>();
    for (const ev of trends.byEvent) {
      if (ev.event === 'page_view') continue;
      const m = eventModule(ev.event);
      const row = byModule.get(m) ?? { eventos: 0, vistos: 0 };
      row.eventos += ev.count;
      byModule.set(m, row);
    }
    const views = new Map(trends.pageViewsByModule.map((p) => [p.module, p.views]));
    return [...byModule.entries()]
      .map(([module, row]) => ({ module: moduleLabel(module), eventos: row.eventos, vistas: views.get(module) ?? 0 }))
      .sort((a, b) => b.eventos - a.eventos)
      .slice(0, 10);
  }, [trends]);

  /** Top modules by page views, with per-module action count (0 = module seen but never used). */
  const engagement = useMemo(() => {
    if (!trends) return [];
    const actions = new Map<string, number>();
    for (const ev of trends.byEvent) {
      if (ev.event === 'page_view') continue;
      const m = eventModule(ev.event);
      actions.set(m, (actions.get(m) ?? 0) + ev.count);
    }
    const rows = trends.pageViewsByModule.map((p) => ({
      module: moduleLabel(p.module), vistas: p.views, acciones: actions.get(p.module) ?? 0,
    }));
    // Include action-only modules with no recorded views yet.
    const seen = new Set(trends.pageViewsByModule.map((p) => p.module));
    for (const [m, count] of actions) {
      if (!seen.has(m)) rows.push({ module: moduleLabel(m), vistas: 0, acciones: count });
    }
    return rows.sort((a, b) => (b.vistas + b.acciones) - (a.vistas + a.acciones)).slice(0, 10);
  }, [trends]);

  /** Per-module daily series for the top 5 action events. */
  const topEventSeries = useMemo(() => {
    if (!trends) return [];
    const top = trends.byEvent.filter((e) => e.event !== 'page_view').slice(0, 5);
    return axis.map((date) => {
      const row: Record<string, string | number> = { date: shortDate(date) };
      for (const ev of top) row[moduleLabel(eventModule(ev.event))] = ev.daily[date] ?? 0;
      return row;
    });
  }, [trends, axis]);

  const totalByRole = useMemo(() => (trends?.byRole ?? []).reduce((a, r) => a + r.count, 0), [trends]);
  const totalByDevice = useMemo(() => (trends?.byDevice ?? []).reduce((a, d) => a + d.count, 0), [trends]);

  const topEventKeys = useMemo(() => {
    if (!trends) return [] as string[];
    return trends.byEvent.filter((e) => e.event !== 'page_view').slice(0, 5).map((e) => moduleLabel(eventModule(e.event)));
  }, [trends]);

  if (loading) return <div className="flex items-center justify-center min-h-full bg-[#1a1a1a]"><Loader2 className="w-8 h-8 text-[#36CCD8] animate-spin" /></div>;
  if (error) return (
    <div className="flex flex-col items-center justify-center min-h-full bg-[#1a1a1a] gap-4">
      <AlertCircle className="w-12 h-12 text-red-400" />
      <p className="text-gray-300 text-lg">{error}</p>
      <button onClick={() => void fetchAll(days)} className="px-4 py-2 bg-[#36CCD8] text-black font-semibold rounded-lg">Reintentar</button>
    </div>
  );

  const hasData = (trends?.total ?? 0) > 0;
  const totalViews = trends?.pageViewsByModule.reduce((a, p) => a + p.views, 0) ?? 0;
  const peakUsers = trends?.activeUsers.reduce((a, u) => Math.max(a, u.users), 0) ?? 0;

  return (
    <div className="min-h-full bg-[#1a1a1a] p-6">
      <div className="max-w-6xl mx-auto">
        <header className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/home/dashboard')} className="p-2 text-gray-400 hover:text-white rounded-lg"><ArrowLeft className="w-5 h-5" /></button>
            <div>
              <h1 className="text-3xl font-bold text-[var(--v-text-primary)] flex items-center gap-2"><BarChart3 className="w-7 h-7 text-[#36CCD8]" />Uso del producto</h1>
              <p className="text-gray-400">Qué módulos se usan, dónde se cae y cuántos usuarios activos hay</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Crown className="w-6 h-6 text-yellow-400" />
            <div className="flex rounded-lg border border-[#3a3a3a] overflow-hidden">
              {([7, 30] as const).map((d) => (
                <button key={d} onClick={() => setDays(d)} className={`px-3 py-1.5 text-sm ${days === d ? 'bg-[#36CCD8] text-black font-semibold' : 'text-gray-400 hover:text-white'}`}>{d}d</button>
              ))}
            </div>
          </div>
        </header>

        {!hasData ? (
          <div className="bg-[#2a2a2a] rounded-xl border border-[#3a3a3a] p-12 text-center">
            <BarChart3 className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-lg text-gray-300 font-semibold">Aún no hay datos suficientes</p>
            <p className="text-gray-500 mt-2 max-w-md mx-auto text-sm">
              El seguimiento de uso empezó a registrar eventos hace poco. Los datos aparecen aquí en cuanto los usuarios navegan y usan módulos (vistas de página y acciones por módulo). Vuelve a revisar en unos días.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <StatCard icon={<Activity />} label="Eventos totales" value={(trends?.total ?? 0).toLocaleString()} sub={`Últimos ${days} días`} />
              <StatCard icon={<Eye />} label="Vistas de página" value={totalViews.toLocaleString()} sub="page_view" />
              <StatCard icon={<Users />} label="Usuarios activos pico" value={peakUsers.toLocaleString()} sub="Usuarios distintos por día" />
              <StatCard icon={<CalendarRange />} label="Módulos con uso" value={String(moduleUsage.length)} sub="Con eventos de acción" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <section className="bg-[#2a2a2a] rounded-xl border border-[#3a3a3a] p-5">
                <h2 className="text-lg font-bold text-white mb-1">Actividad diaria</h2>
                <p className="text-gray-500 text-xs mb-4">Eventos totales por día y usuarios activos distintos</p>
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={dailySeries}>
                    <defs>
                      <linearGradient id="usageEvents" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#36CCD8" stopOpacity={0.35} />
                        <stop offset="95%" stopColor="#36CCD8" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#3a3a3a" />
                    <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 11 }} axisLine={false} tickLine={false} minTickGap={24} />
                    <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: 12, color: '#fff' }} />
                    <Legend wrapperStyle={{ paddingTop: 10 }} />
                    <Area type="monotone" dataKey="eventos" stroke="#36CCD8" fill="url(#usageEvents)" strokeWidth={2} name="Eventos" />
                    <Line type="monotone" dataKey="usuarios" stroke="#F59E0B" strokeWidth={2} dot={false} name="Usuarios activos" />
                  </AreaChart>
                </ResponsiveContainer>
              </section>

              <section className="bg-[#2a2a2a] rounded-xl border border-[#3a3a3a] p-5">
                <h2 className="text-lg font-bold text-white mb-1">Actividad por evento (top 5)</h2>
                <p className="text-gray-500 text-xs mb-4">Serie diaria de los eventos de acción más usados</p>
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={topEventSeries}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#3a3a3a" />
                    <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 11 }} axisLine={false} tickLine={false} minTickGap={24} />
                    <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: 12, color: '#fff' }} />
                    <Legend wrapperStyle={{ paddingTop: 10 }} />
                    {topEventKeys.map((key, i) => (
                      <Area key={key} type="monotone" dataKey={key} stroke={['#36CCD8', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444'][i % 5]} fill={['#36CCD8', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444'][i % 5]} fillOpacity={0.12} strokeWidth={2} />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              </section>
            </div>

            <section className="bg-[#2a2a2a] rounded-xl border border-[#3a3a3a] p-5 mb-6">
              <h2 className="text-lg font-bold text-white mb-1">Por tipo de usuario</h2>
              <p className="text-gray-500 text-xs mb-4">Eventos por rol (del JWT, servidor) y tipo de dispositivo</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-gray-400 text-xs font-semibold uppercase tracking-wide mb-2">Roles</p>
                  {totalByRole === 0 ? <EmptyChart /> : (
                    <div className="flex flex-wrap gap-2">
                      {(trends?.byRole ?? []).map((r) => (
                        <div key={r.role} className="flex items-center gap-2 rounded-full border border-[#3a3a3a] bg-[#232323] px-3 py-1.5">
                          <span className="text-sm text-gray-200">{roleLabel(r.role)}</span>
                          <span className="text-sm font-bold text-[#36CCD8]">{r.count.toLocaleString()}</span>
                          <span className="text-xs text-gray-500">{totalByRole ? `${Math.round((r.count / totalByRole) * 100)}%` : ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-gray-400 text-xs font-semibold uppercase tracking-wide mb-2">Dispositivos</p>
                  {totalByDevice === 0 ? <EmptyChart /> : (
                    <div className="h-[72px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={(trends?.byDevice ?? []).map((d) => ({ name: deviceLabel(d.device), eventos: d.count }))} layout="vertical" margin={{ left: 10 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#3a3a3a" horizontal={false} />
                          <XAxis type="number" hide allowDecimals={false} />
                          <YAxis type="category" dataKey="name" width={90} tick={{ fill: '#d1d5db', fontSize: 11 }} axisLine={false} tickLine={false} />
                          <Tooltip contentStyle={{ background: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: 12, color: '#fff' }} />
                          <Bar dataKey="eventos" name="Eventos" fill="#8B5CF6" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              </div>
            </section>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <section className="bg-[#2a2a2a] rounded-xl border border-[#3a3a3a] p-5">
                <h2 className="text-lg font-bold text-white mb-1">Módulos más usados</h2>
                <p className="text-gray-500 text-xs mb-4">Acciones de creación/uso por módulo (top 10)</p>
                {moduleUsage.length === 0 ? <EmptyChart /> : (
                  <ResponsiveContainer width="100%" height={Math.max(200, moduleUsage.length * 34)}>
                    <BarChart data={moduleUsage} layout="vertical" margin={{ left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#3a3a3a" horizontal={false} />
                      <XAxis type="number" tick={{ fill: '#9ca3af', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <YAxis type="category" dataKey="module" width={130} tick={{ fill: '#d1d5db', fontSize: 11 }} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={{ background: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: 12, color: '#fff' }} />
                      <Bar dataKey="eventos" name="Acciones" fill="#36CCD8" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </section>

              <section className="bg-[#2a2a2a] rounded-xl border border-[#3a3a3a] p-5">
                <h2 className="text-lg font-bold text-white mb-1">Dónde se caen: vistas vs. acciones</h2>
                <p className="text-gray-500 text-xs mb-4">Módulos visitados pero nunca usados aparecen con acciones en 0</p>
                {engagement.length === 0 ? <EmptyChart /> : (
                  <ResponsiveContainer width="100%" height={Math.max(200, engagement.length * 34)}>
                    <BarChart data={engagement} layout="vertical" margin={{ left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#3a3a3a" horizontal={false} />
                      <XAxis type="number" tick={{ fill: '#9ca3af', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <YAxis type="category" dataKey="module" width={130} tick={{ fill: '#d1d5db', fontSize: 11 }} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={{ background: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: 12, color: '#fff' }} />
                      <Legend wrapperStyle={{ paddingTop: 10 }} />
                      <Bar dataKey="vistas" name="Vistas" fill="#2DB8C4" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="acciones" name="Acciones" fill="#F59E0B" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </section>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const EmptyChart = () => <div className="h-[200px] flex items-center justify-center text-sm text-gray-500">Sin datos para el rango seleccionado</div>;

const StatCard = ({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub?: string }) => (
  <div className="bg-[#2a2a2a] rounded-xl border border-[#3a3a3a] p-5">
    <div className="text-[#36CCD8] mb-3">{icon}</div>
    <p className="text-gray-400 text-sm">{label}</p>
    <p className="text-2xl font-bold text-white">{value}</p>
    {sub && <p className="text-gray-500 text-xs mt-1">{sub}</p>}
  </div>
);

export default SuperAdminUsage;
